import { unmarshal as createConfig } from '@ogury/unmarshaller';
import eventBus from 'common/event-handling/event-bus.model';
import { ImpressionSentEvent } from 'common/event-handling/events';
import { BROWSERS } from './browser-utils';
import CONFIG from './config';
import { waitForReady, waitForViewability } from './mraid-utils';
import { MraidProvider } from './mraid/mraid-provider';
import { preloadAsset } from './network-utils';
import { createTranslator } from './next-translator';
import {
  initOmidAdEvents,
  sendOmidImpression,
  sendOmidLoaded,
  setOmidImpressionDelay
} from './omid/omid';
import { incrementCurrentRenderTime, startRenderTime } from './render-time';
import { initSentry, logMessage } from './sentry';
import {
  checkAndSendNoUserIdTrack,
  initAnalytics,
  sendCreativeLoadInitTrack
} from './TrackAnalyticsHelper';
import { updateQueryStringParameters } from './url-utils';
import { generateLink } from './utils';
import { initMobx } from './mobx-utils';
import { DebugLog } from './debug-log';
import { encapsulateClickUrlIfNeeded } from './click-utils';
import { isFeatureEnable } from './features-enabling';

let actions: Partial<FormatActions> | null = null;
let isPreviouslyViewable = false;

export interface FormatContext<ConfigType, TranslationsKeys> {
  config: ConfigType;
  actions: FormatActions;
  translate: (
    path: TranslationsKeys,
    args?: TranslatorMacroReplace | undefined
  ) => string;
  context: AdContext;
}

export function onFormatViewable({
  preventOmid = false,
  preventTrackId = false
} = {}) {
  eventBus.publish(new ImpressionSentEvent());

  if (!preventOmid) {
    sendOmidImpression();
  }
  if (!preventTrackId) {
    checkAndSendNoUserIdTrack();
  }
  // sendDisplayTrack('display');
}

export function onFormatClick(
  adOptions: FormatOptions,
  clickUrl: string,
  { useOnClickCallback = true } = {}
) {
  let clickUrlUpdated: string = updateQueryStringParameters(clickUrl, {
    source: 'click'
  });

  clickUrlUpdated = encapsulateClickUrlIfNeeded(clickUrlUpdated);

  if (
    useOnClickCallback &&
    typeof adOptions.callbacks.onClick !== 'undefined'
  ) {
    adOptions.callbacks.onClick(adOptions.context);
  }

  if (typeof window.mraid.open === 'undefined') {
    logMessage(
      `"window mraid open is undefined for campaignId : ${adOptions.context.campaignId} and apiKey : ${adOptions.context.apiKey}"`
    );
    const link: HTMLElement = generateLink(clickUrlUpdated);
    link.click();
  } else {
    window.mraid.open(clickUrlUpdated);
  }
}

function debugCall(functionName: string) {
  // tslint:disable-next-line
  console.error(
    `The default ${functionName} has been called, this is an error.
This method is maybe not exposed, on purpose. Check other methods`
  );
}

const defaultFormatActions: FormatActions = {
  onUserClick: () => debugCall('onUserClick'),
  onImpression: () => debugCall('onImpression'),
  onClose: () => debugCall('onClose'),
  onOptout: () => debugCall('onOptout'),
  onAccomplish: () => debugCall('onAccomplish'),
  onFormatClose: () => debugCall('onFormatClose'),
  onUserClose: () => debugCall('onUserClose'),
  onFormatPrint: () => debugCall('onFormatPrint'),
  onUserOptout: () => debugCall('onUserOptout'),
  onAutoClose: () => debugCall('onAutoClose')
};
const defaultFormatVideoActions: FormatVideoActions = {
  ...defaultFormatActions,
  onToggleMute: () => debugCall('onToggleMute'),
  onVideoError: () => debugCall('onVideoError'),
  onEvent: () => debugCall('onEvent'),
  onVideoEvent: () => debugCall('onVideoEvent'),
  onProgress: () => debugCall('onProgress'),
  onComplete: () => debugCall('onComplete')
};

interface PrepareFormatInfos<T> {
  formatName: FormatName;
  options: FormatOptions;
  formatUnmarshaller: AnyObject;
  translations?: TranslationsWithEn<T>;
  createActions?:
    | ((adOptions: FormatOptions, config: any) => Partial<FormatActions>)
    | null;
  isVideo?: boolean;
  orientation?: Orientation;
  useCustomClose?: boolean;
  isMraidPolyfilled?: boolean;
}

export function getFormatConfig(
  options: FormatOptions,
  formatUnmarshaller: AnyObject
) {
  DebugLog.log('Get format config', options);
  let formatConfig: object;

  // Flow for Display redirect/image
  if (options.context.adUnitType && options.context.creativeSource) {
    let key: string = `${options.context.adUnitType}`;

    if (
      options.context.adUnitType === 'in_article' &&
      options.context.inArticleType
    ) {
      key += `/${options.context.inArticleType}`;
    }

    key += `/${options.context.creativeSource}`;

    if (formatUnmarshaller[key]) {
      DebugLog.log('Format config founded', formatUnmarshaller[key]);
      return formatUnmarshaller[key];
    }
  }

  // legacy flow
  if (
    options.context.adUnitType &&
    options.context.adUnitType in formatUnmarshaller
  ) {
    formatConfig = formatUnmarshaller[options.context.adUnitType];

    if (
      formatUnmarshaller[options.context.adUnitType] &&
      formatUnmarshaller[options.context.adUnitType].config
    ) {
      formatConfig = formatUnmarshaller[options.context.adUnitType].config;
    }
  } else {
    formatConfig =
      formatUnmarshaller?.[Object.keys(formatUnmarshaller)?.[0]] || {};
  }

  DebugLog.log('Format config founded', formatConfig);

  return formatConfig;
}

export async function prepareFormat<
  TranslationsType,
  TranslationsKeys,
  ConfigType extends FormatConfigType<TranslationsType>
>({
  formatName,
  options,
  formatUnmarshaller,
  translations,
  createActions = null,
  isVideo = false,
  orientation = 'auto',
  useCustomClose = true
}: PrepareFormatInfos<TranslationsType>): Promise<
  FormatContext<ConfigType, TranslationsKeys>
> {
  DebugLog.log('Prepare format', { formatName, options });

  initSentry(formatName, options.context);

  window.OG_DEVICE_TYPE = options.context?.deviceType || undefined;
  const desc = Object.getOwnPropertyDescriptor(window, 'MRAID_ENV');
  if (!desc || desc.writable || desc.configurable) {
    window.MRAID_ENV = window.MRAID_ENV || {};
  }
  // else MRAID_ENV is read-only

  initMobx();

  const formatConfig: object = getFormatConfig(options, formatUnmarshaller);
  const config: ConfigType = createConfig(options.getParameter, formatConfig);
  DebugLog.log('Format config unmarshalled', config);

  if (config.orientation) {
    orientation = config.orientation;
  }

  initAnalytics(formatName, config, options.context);
  sendCreativeLoadInitTrack();

  // TODO in futur for video, get back this informations from VASTS :
  const javascriptResources: JavascriptResource[] = [];
  if (config.omid && config.omid.trackingEnabled) {
    // Mraid creative is injected on body level in contrary to others formats which are completely injected on #root
    initOmidAdEvents(
      ['Display redirect'].indexOf(formatName) > -1
        ? document.body
        : options.container,
      {
        javascriptResources,
        omidTrackers: config.omid.trackers,
        enableOgyOmidTag: config.omid.enableOgyTag,
        enableOmidViewabilityTracks: options.context.enableOmidViewabilityTracks
      }
    );
    setOmidImpressionDelay(config.omid.impressionDelay);
    sendOmidLoaded();
  }

  if (createActions) {
    actions = createActions(options, config);
  }

  let translate = ((() => {}) as unknown) as (
    path: TranslationsKeys,
    args?: TranslatorMacroReplace | undefined
  ) => string;

  if (typeof translations !== 'undefined') {
    translate = createTranslator<TranslationsType, TranslationsKeys>(
      config.locale,
      translations,
      config.customTranslations
    );
  }

  await new MraidProvider().provideMraid(window, {
    isMraidCompliant: config.isMraidCompliant,
    appExchange: options.context._appExchange,
    dsp: options.context.dsp
  });

  initHandlers(formatName, orientation, useCustomClose);
  return {
    actions: {
      ...(isVideo ? defaultFormatVideoActions : defaultFormatActions),
      ...actions
    },
    config,
    translate,
    context: {
      ...options.context,
      isMraidPolyfilled: window.mraid.__ogyPolyfill
    }
  };
}

export function preloadBrowser(browserConfig?: BrowserConfigType) {
  if (
    isFeatureEnable('oguryBrowser') &&
    browserConfig &&
    browserConfig.preload &&
    browserConfig.type !== BROWSERS.CHROME_TAB &&
    browserConfig.type !== BROWSERS.DEFAULT_BROWSER
  ) {
    // while not required, this helps speeding up the browser display
    preloadAsset(CONFIG.SDK3_BROWSER_URL);
  }
}

function onMraidReady(orientation: Orientation, useCustomClose: boolean) {
  window.mraid.useCustomClose(useCustomClose);
  if (orientation !== 'auto') {
    if (window.mraid.setOrientationProperties) {
      window.mraid.setOrientationProperties({
        allowOrientationChange: false,
        forceOrientation: orientation
      });
    }
  }
}

function onViewabilityChange(isCurrentViewable: boolean) {
  if (isCurrentViewable && actions && actions.onImpression) {
    actions.onImpression();
  }

  if (isCurrentViewable && isCurrentViewable !== isPreviouslyViewable) {
    startRenderTime();
  }
  if (!isCurrentViewable && isCurrentViewable !== isPreviouslyViewable) {
    incrementCurrentRenderTime();
  }

  isPreviouslyViewable = isCurrentViewable;
}

function initHandlers(
  formatName: string,
  orientation: Orientation,
  useCustomClose: boolean
): void {
  waitForReady(() => onMraidReady(orientation, useCustomClose));

  if (['Display image'].indexOf(formatName) === -1) {
    waitForViewability(onViewabilityChange);
  }
}
